import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calculator, Shield, Zap, Target, LogIn, Home } from 'lucide-react';
import CompanyLogo from '../components/CompanyLogo';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../i18n/LanguageContext';
import { COMPANY } from '../config/company';

const LandingPage = () => {
  const { language } = useLanguage();

  const features = [
    {
      icon: Target,
      title: language === 'es' ? 'Gestión de Leads' : 'Lead Management',
      description: language === 'es' 
        ? 'Gestione leads con seguimiento de estado, notas y documentos'
        : 'Manage leads with status tracking, notes, and documents'
    },
    {
      icon: Calculator,
      title: language === 'es' ? 'Presupuestos Profesionales' : 'Professional Estimates',
      description: language === 'es' 
        ? 'Cree presupuestos detallados con nuestra biblioteca completa'
        : 'Create detailed estimates with our comprehensive library'
    },
    {
      icon: Zap,
      title: language === 'es' ? 'Rápido y Eficiente' : 'Fast & Efficient',
      description: language === 'es'
        ? 'Agregue categorías completas de partidas con un solo clic'
        : 'Add complete categories of line items with a single click'
    },
    {
      icon: Shield,
      title: language === 'es' ? 'Bilingüe' : 'Bilingual',
      description: language === 'es'
        ? 'Soporte completo en inglés y español'
        : 'Full English and Spanish language support'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <nav className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <CompanyLogo className="h-10 sm:h-12 w-auto" />
            </div>
            <div className="flex items-center space-x-3">
              <LanguageToggle />
              <Link to="/homeowner/login">
                <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                  <Home className="mr-2 h-4 w-4" />
                  {language === 'es' ? 'Clientes' : 'Client Login'}
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-100">
                  <LogIn className="mr-2 h-4 w-4" />
                  {language === 'es' ? 'Admin' : 'Admin Login'}
                </Button>
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            {language === 'es' ? 'Presupuestos de Construcción' : 'Construction Estimates'}
            <span style={{ color: COMPANY.primaryColor }}> {language === 'es' ? 'Profesionales' : 'Made Simple'}</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-10">
            {language === 'es' 
              ? 'Gestione leads, cree presupuestos detallados y conviértalos en clientes. Todo en un solo lugar.'
              : 'Manage leads, create detailed estimates, and convert them to clients. All in one place.'}
          </p>
          <Link to="/login">
            <Button size="lg" style={{ backgroundColor: COMPANY.primaryColor }} className="text-white px-10 py-6 text-lg shadow-lg hover:shadow-xl transition-shadow" data-testid="get-started-btn">
              <LogIn className="mr-2 h-5 w-5" />
              {language === 'es' ? 'Iniciar Sesión' : 'Get Started'}
            </Button>
          </Link>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 sm:px-6 py-12 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <Card key={index} className="bg-white border-gray-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${COMPANY.primaryColor}20` }}>
                  <feature.icon className="h-7 w-7" style={{ color: COMPANY.primaryColor }} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <p>&copy; {new Date().getFullYear()} ProEstimate. {language === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.'}</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
